import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ClienteDeLaLibreta } from './factura-electronica';

/** Lo que la DIAN tiene de un comprador: basta para no teclear el nombre ni el correo. */
export interface Adquirente {
    nombre: string;
    correo?: string | null;
}

/**
 * Autocompletar el formulario de factura (F4). Pregunta a -mt, que es el ÚNICO punto que decide de dónde sale el
 * comprador: hoy la DIAN (vía Factus); mañana, primero la libreta de clientes del negocio (el mini CRM que planea ECM)
 * y después la DIAN, sin cambiar nada aquí. Nunca estorba el cobro: sin red, sin facturación o sin resultado devuelve
 * null y el cajero escribe a mano.
 */
@Injectable({ providedIn: 'root' })
export class AdquirenteService {
    private readonly http = inject(HttpClient);

    async buscar(tipoDocumento: string, numero: string): Promise<Adquirente | null> {
        if (!numero.trim()) return null;
        try {
            const r = await firstValueFrom(this.http.get<Adquirente | null>(`${environment.apiBaseUrl}/api/facturacion/adquirente`, {
                params: { tipo: tipoDocumento, numero: numero.trim() },
            }));
            return r && r.nombre ? r : null;
        } catch {
            return null;
        }
    }

    /**
     * ¿Este negocio factura hoy? true / false, o null si no se pudo saber (sin red): entonces no se avisa nada, porque
     * no se sabe. Con false, el cobro lo dice: la venta queda sin factura (29/09, antes pasaba en silencio).
     */
    async facturaActiva(): Promise<boolean | null> {
        try {
            const r = await firstValueFrom(this.http.get<{ activa: boolean }>(`${environment.apiBaseUrl}/api/facturacion/estado`));
            return typeof r?.activa === 'boolean' ? r.activa : null;
        } catch {
            return null;
        }
    }

    /**
     * M34 C1 · la alerta del rango de numeración de facturas (`rangos.factura.alerta` de `/api/facturacion/estado`, -mt
     * 3f822a41): VENCIDO, AGOTADO, POR_AGOTARSE, POR_VENCER, NO_ENCONTRADO o null. Sin red o con un -mt anterior, null.
     */
    async alertaDeRango(): Promise<string | null> {
        try {
            const r = await firstValueFrom(this.http.get<{ rangos?: { factura?: { alerta?: string | null } | null } | null }>(
                `${environment.apiBaseUrl}/api/facturacion/estado`));
            return r?.rangos?.factura?.alerta ?? null;
        } catch {
            return null;
        }
    }

    /**
     * «Guardar cliente»: el cajero lo pidió, así que va a la libreta del negocio para autocompletar la próxima vez. Nunca
     * estorba el cobro: si ya existía (409) o no hay red, no pasa nada.
     */
    async guardarEnLaLibreta(cliente: ClienteDeLaLibreta): Promise<void> {
        try {
            await firstValueFrom(this.http.post(`${environment.apiBaseUrl}/api/clientes`, cliente));
        } catch {
            // 409 YA_EXISTE o sin red: la venta ya salió; la libreta es una comodidad.
        }
    }
}
